import { type Element, type Root } from "hast";
import { toText } from "hast-util-to-text";
import { parse } from "space-separated-tokens";
import { type Plugin } from "unified";
import { visitParents } from "unist-util-visit-parents";

interface CodeInstance {
  /**
   * The mermaid diagram.
   */
  diagram: string;

  /**
   * The inclusive ancestors of the element to process.
   */
  ancestors: Element[];
}

/**
 * A regular expression to test for non-whitespace characters.
 */
const nonWhitespacePattern = /\w/;

/**
 * Check if a hast element has the `language-mermaid` class name.
 *
 * @param element
 *   The hast element to check.
 * @param strategy
 *   The mermaid strategy to use.
 * @returns
 *   Whether or not the element has the `language-mermaid` class name.
 */
function isMermaidElement(element: Element): boolean {
  let mermaidClassName: string;

  if (element.tagName === "pre") {
    return false;
  } else if (element.tagName === "code") {
    mermaidClassName = "language-mermaid";
  } else {
    return false;
  }

  let className = element.properties?.className;
  if (typeof className === "string") {
    className = parse(className);
  }

  if (!Array.isArray(className)) {
    return false;
  }

  return className.includes(mermaidClassName);
}

interface RehypeMermaidOptions {
  strategy?: string;
}

/**
 * A [rehype](https://rehype.js.org) plugin to render [mermaid](https://mermaid-js.github.io)
 * diagrams.
 *
 * @param options
 *   Options that may be used to tweak the output.
 */
const rehypeMermaid: Plugin<[RehypeMermaidOptions], Root> = (options) => {
  return (ast: any, file: any) => {
    const instances: CodeInstance[] = [];

    visitParents(ast, "element", (node, ancestors) => {
      if (!isMermaidElement(node)) {
        return;
      }

      const parent = ancestors.at(-1)!;
      let inclusiveAncestors = ancestors as Element[];

      // This is <code> wrapped in a <pre> element.
      if (parent.type === "element" && parent.tagName === "pre") {
        for (const child of parent.children) {
          // We allow whitespace text siblings, but any other siblings mean we don’t process the
          // diagram.
          if (child.type === "text") {
            if (nonWhitespacePattern.test(child.value)) {
              return;
            }
          } else if (child !== node) {
            return;
          }
        }
      } else {
        inclusiveAncestors = [...inclusiveAncestors, node];
      }

      instances.push({
        diagram: toText(node, { whitespace: "pre" }),
        ancestors: inclusiveAncestors,
      });
    });

    // Nothing to do. No need to start a browser in this case.
    if (!instances.length) {
      return;
    }

    for (const { ancestors, diagram } of instances) {
      const parent = ancestors.at(-2)!;
      const node = ancestors.at(-1)!;

      parent.children[parent.children.indexOf(node)] = {
        type: "element",
        tagName: "pre",
        properties: {
          className: ["mermaid"],
        },
        children: [{ type: "text", value: diagram }],
      };
    }
  };
};

export default rehypeMermaid;
